import { ArrowLeftIcon, BrainIcon, CompassIcon, SparklesIcon, type LucideIcon } from 'lucide-react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { assetUrl } from '@/lib/asset-url';
import {
  accentBorder,
  accentGlow,
  accentSoftBg,
  accentText,
  accentWash
} from '@/lib/product-accents';
import type { ProductPageContent } from '@/content/products';

const iconById: Record<ProductPageContent['id'], LucideIcon> = {
  nextonvera: BrainIcon,
  'cancer-navigator': CompassIcon,
  'grace-ai': SparklesIcon
};

const statusBadgeClass: Record<ProductPageContent['statusTone'], string> = {
  live: 'border-cta/40 bg-cta/15 text-cta',
  pilot: 'border-primary/40 bg-primary/15 text-primary',
  dev: 'border-urgency/40 bg-urgency/15 text-urgency'
};

/** Bevelled 3D metal-cast plate styles, keyed by the hero frame finish. */
const metalFrameStyles: Record<NonNullable<ProductPageContent['heroFrame']>, CSSProperties> = {
  platinum: {
    backgroundImage:
      'linear-gradient(160deg, #fdfdfc 0%, #f1eff4 28%, #ded9e6 50%, #c8c1d2 55%, #eae6ee 72%, #f8f7f4 100%)',
    boxShadow:
      '0 18px 32px -14px rgba(15, 23, 42, 0.45), 0 6px 12px -6px rgba(15, 23, 42, 0.3), 0 1px 2px rgba(15, 23, 42, 0.2), inset 0 2px 0 rgba(255, 255, 255, 0.95), inset 0 -3px 6px rgba(15, 23, 42, 0.22), inset 2px 0 4px rgba(255, 255, 255, 0.55), inset -2px 0 4px rgba(15, 23, 42, 0.12)',
    border: '1px solid rgba(132, 125, 148, 0.5)'
  },
  gold: {
    backgroundImage:
      'linear-gradient(160deg, #fdfbf4 0%, #f8f1da 28%, #ecdcae 50%, #d9c07c 55%, #f2e3b6 72%, #fbf6e4 100%)',
    boxShadow:
      '0 18px 32px -14px rgba(64, 48, 10, 0.4), 0 6px 12px -6px rgba(64, 48, 10, 0.28), 0 1px 2px rgba(64, 48, 10, 0.18), inset 0 2px 0 rgba(255, 253, 245, 0.95), inset 0 -3px 6px rgba(96, 72, 18, 0.24), inset 2px 0 4px rgba(255, 250, 235, 0.6), inset -2px 0 4px rgba(96, 72, 18, 0.12)',
    border: '1px solid rgba(150, 120, 52, 0.5)'
  }
};

export function ProductHero({ product }: { product: ProductPageContent }) {
  const Icon = iconById[product.id];
  const hasHeroImage = Boolean(product.heroImage);
  const containImage = product.heroImageFit === 'contain';
  const metalFrame = product.heroFrame === 'platinum' || product.heroFrame === 'gold';

  return (
    <header className="relative overflow-hidden px-4 pt-10 pb-14 sm:px-6 sm:pt-12 sm:pb-16 lg:px-8">
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 bg-gradient-to-br opacity-90',
          accentWash[product.accent]
        )}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            'radial-gradient(circle at 18% 22%, color-mix(in oklab, var(--primary) 18%, transparent), transparent 42%), radial-gradient(circle at 82% 12%, color-mix(in oklab, var(--cta) 14%, transparent), transparent 36%)'
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl">
        <Link
          to={product.backHref}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring mb-8 inline-flex items-center gap-2 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none"
        >
          <ArrowLeftIcon className="size-4" aria-hidden />
          {product.backLabel}
        </Link>

        <div
          className={cn(
            'grid gap-10 lg:gap-12',
            hasHeroImage
              ? cn(
                  'lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]',
                  containImage ? 'lg:items-end' : 'lg:items-stretch'
                )
              : 'items-start lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.9fr)]'
          )}
        >
          <div className="flex min-h-0 flex-col">
            <div className="mb-5 flex flex-wrap items-center gap-3">
              <p className={cn('text-xs font-medium tracking-[0.2em] uppercase', accentText[product.accent])}>
                {product.indexLabel} · {product.eyebrow}
              </p>
              <Badge
                variant="outline"
                className={cn('rounded-full px-3 py-1 text-xs font-medium', statusBadgeClass[product.statusTone])}
              >
                {product.status}
              </Badge>
            </div>

            <h1 className="font-display text-foreground text-4xl font-semibold tracking-tight text-balance sm:text-5xl md:text-6xl">
              {product.name}
            </h1>
            <p className="font-display text-foreground/90 mt-4 max-w-2xl text-xl font-medium tracking-tight text-balance sm:text-2xl">
              {product.tagline}
            </p>
            <p className="text-muted-foreground mt-5 max-w-2xl text-base leading-relaxed sm:text-lg">
              {product.pitch}
            </p>

            {hasHeroImage ? (
              <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <Button asChild size="lg" variant={product.cta.primary.variant} className="sm:min-w-44">
                  <Link to={product.cta.primary.href}>{product.cta.primary.label}</Link>
                </Button>
                <Button asChild size="lg" variant={product.cta.secondary.variant} className="sm:min-w-44">
                  <Link to={product.cta.secondary.href}>{product.cta.secondary.label}</Link>
                </Button>
              </div>
            ) : null}
          </div>

          {hasHeroImage ? (
            <figure
              style={
                product.heroFrame ? metalFrameStyles[product.heroFrame] : undefined
              }
              className={cn(
                'border-foreground/20 rounded-2xl border-[1.5px] bg-gradient-to-b from-white via-card to-muted/70',
                'shadow-[0_18px_40px_-18px_rgba(15,23,42,0.35),0_6px_14px_-8px_rgba(15,23,42,0.25),inset_0_1px_0_rgba(255,255,255,0.9),inset_0_-2px_6px_rgba(15,23,42,0.08)]',
                'transition-transform duration-300 motion-reduce:transition-none motion-reduce:transform-none',
                metalFrame
                  ? 'relative flex items-center justify-center rounded-xl p-4 [transform:perspective(1200px)_rotateX(2.5deg)] sm:p-5'
                  : containImage
                    ? 'self-end'
                    : 'relative flex min-h-56 items-center justify-center p-3 sm:p-4 lg:min-h-full lg:[transform:perspective(1400px)_rotateX(1.5deg)_rotateY(-2.5deg)]'
              )}
            >
              <img
                src={assetUrl(product.heroImage ?? '')}
                alt={product.heroImageAlt ?? ''}
                className={
                  metalFrame
                    ? 'block h-auto w-full rounded-xl object-contain object-center shadow-[0_10px_24px_-12px_rgba(15,23,42,0.45)] ring-1 ring-foreground/10'
                    : containImage
                      ? 'block h-auto w-full'
                      : 'block w-full rounded-xl object-contain object-center shadow-[0_10px_24px_-12px_rgba(15,23,42,0.45)] ring-1 ring-foreground/10'
                }
                decoding="async"
                fetchPriority="high"
              />
            </figure>
          ) : (
            <div
              className={cn(
                'border-border/70 bg-card/60 relative hidden min-h-56 overflow-hidden rounded-2xl border p-8 backdrop-blur-sm sm:block',
                accentGlow[product.accent],
                accentBorder[product.accent]
              )}
              aria-hidden
            >
              <div className={cn('absolute inset-0 bg-gradient-to-br opacity-80', accentWash[product.accent])} />
              <div className="relative z-10 flex h-full flex-col justify-between">
                <div
                  className={cn(
                    'flex size-14 items-center justify-center rounded-2xl border',
                    accentSoftBg[product.accent],
                    accentBorder[product.accent]
                  )}
                >
                  <Icon className={cn('size-7', accentText[product.accent])} />
                </div>
                <div>
                  <p className={cn('text-xs font-medium tracking-[0.18em] uppercase', accentText[product.accent])}>
                    NextOnVera
                  </p>
                  <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                    Funded through Tour De Cure · Public product briefing
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
